import React, { useState, useEffect, useRef } from 'react'
import { Modal } from 'antd'
import { isBrowser, isTablet } from 'react-device-detect'
import { colors } from '@lmc/common/constants'
import './styles.less'
import SignatureModalBody from './Body'
import Footer from './Footer'
import { getNextStep } from './utils'
import FormState from '../FormState.type'
import { Heading, Progress } from '@lmc/ui-kit'
import { getPercentage } from './utils'
import { Resident } from '@lmc/ts-common'
import {
  MentalCapacityToConsentSchema,
  type MentalCapacityToConsent,
  type ResidentFetchItemResponse
} from '@lmc/validations'
import requests from 'api'
import { useQuery } from 'react-query'
import useFeatureFlags from 'hooks/useFeatureFlags'

type SignatureModalTypes = {
  confirmText: string
  footerLabel?: string
  isShowing: boolean
  isSubmitting: boolean
  resident: Resident
  onClose: () => void
  onSubmit: (result: object) => void
  onSuccessClose?: () => void
  isGP?: boolean
}

const SignatureModal = ({
  resident,
  isShowing,
  onClose,
  onSubmit,
  isSubmitting,
  footerLabel,
  confirmText,
  onSuccessClose,
  isGP
}: SignatureModalTypes) => {
  const { data: flags } = useFeatureFlags({
    flagNames: ['mental_capacity']
  })
  const mentalCapacityEnabled = flags?.mental_capacity?.enabled

  const { data: residentData } = useQuery(
    ['resident', resident?.id],
    () => requests.residents.fetch({ params: { id: resident?.id } }),
    { enabled: isShowing && Boolean(resident?.id) && mentalCapacityEnabled === true }
  )
  const fetchedResident = residentData?.result as
    | ResidentFetchItemResponse
    | undefined
  const mentalCapacityStatus: MentalCapacityToConsent | undefined =
    mentalCapacityEnabled ? fetchedResident?.mentalCapacityToConsent : undefined

  const [previousSteps, setPreviousSteps] = useState<string[]>([])
  const [formState, setFormState] = useState<FormState>({})
  const [currentStep, setCurrentStep] = useState(isGP ? 'start-clean' : 'start')
  const isStart = currentStep === 'start'
  const capacityInitializedRef = useRef(false)

  useEffect(() => {
    if (isShowing) {
      setPreviousSteps([])
      setFormState({})
      setCurrentStep(isGP ? 'start-clean' : 'start')
      capacityInitializedRef.current = false
    }
  }, [isShowing, isGP])

  useEffect(() => {
    if (
      isShowing &&
      mentalCapacityStatus !== undefined &&
      !capacityInitializedRef.current
    ) {
      capacityInitializedRef.current = true
      const initialCapacity =
        mentalCapacityStatus === MentalCapacityToConsentSchema.enum.yes
          ? true
          : mentalCapacityStatus === MentalCapacityToConsentSchema.enum.no
            ? false
            : undefined
      if (initialCapacity !== undefined) {
        setFormState((prev) => ({ ...prev, capacity: initialCapacity }))
      }
    }
  }, [isShowing, mentalCapacityStatus])

  const onModalClose = () => {
    onClose()
  }

  const onConfirm = () => {
    setCurrentStep('success')
  }

  const onSuccess = () => {
    onSuccessClose?.()
    if (!isGP) {
      onClose()
    }
  }

  const onNext = (next: React.SetStateAction<string>) => {
    setPreviousSteps([...previousSteps, currentStep])
    setCurrentStep(next)
  }

  const goBack = () => {
    if (currentStep === 'start') return
    const prev = previousSteps.slice(-1)[0]
    setPreviousSteps(previousSteps.filter((step) => step !== prev))
    setCurrentStep(prev)
  }

  const stepLookup = {
    capacity: 0,
    lpa: 1,
    witness: 1,
    ability: 1,
    sign: 2,
    confirm: 3
  }

  const onModalSubmit = () => {
    onSubmit({
      formState,
      resident,
      onSuccess: onConfirm,
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      onError: () => {}
    })
  }

  const hasImportantContacts =
    resident.importantContacts && resident.importantContacts.length > 0
  const residentName = `${resident?.preferredName || resident.name.first} ${
    resident.name.last
  }`
  const isDesktop = isBrowser || isTablet
  const ModalFooter = Footer({
    label: isDesktop ? footerLabel : undefined,
    isStart,
    nextStep: getNextStep?.(
      currentStep,
      formState,
      hasImportantContacts || false,
      isGP
    ),
    residentName,
    onClose,
    isSubmitting,
    goBack,
    onSubmit: onModalSubmit,
    onNext,
    currentStep,
    onSuccess,
    isGP
  })

  const styles = {
    bodyContainer: {
      paddingTop: 8,
      paddingBottom: 24,
      borderTop: `1px solid ${colors.lightgrey}`,
      borderBottom: `1px solid ${colors.lightgrey}`
    },
    stepContainer: {
      margin: '0 auto',
      position: 'relative'
    },
    container: {
      maxWidth: isDesktop ? 280 : 140
    },
    headerWrapper: { paddingTop: 15, minHeight: 70 },
    progressContainer: { marginTop: 10, marginBottom: 10 }
  }

  /* @ts-ignore */
  const percentage = getPercentage(stepLookup[currentStep])

  const isSuccessStep = currentStep === 'success'

  const getHeadingTitle = () => {
    if (isSuccessStep) {
      return 'Success!'
    }

    if (isGP) {
      return 'GP Connect Sign'
    }

    return 'Add a Consent Signature'
  }

  return (
    <Modal
      className="signature-modal"
      open={isShowing}
      onCancel={onModalClose}
      footer={ModalFooter}
      maskClosable={false}
      width={700}
    >
      <div style={styles.headerWrapper}>
        <Heading level={3}>{getHeadingTitle()}</Heading>
        {!isGP && !isSuccessStep && (
          <div style={styles.progressContainer}>
            <Progress showInfo={false} percent={percentage} />
          </div>
        )}
      </div>
      <div style={styles.bodyContainer}>
        {/* @ts-ignore */}
        <SignatureModalBody
          isDesktop={isDesktop}
          currentStep={currentStep}
          formState={formState}
          resident={resident}
          confirmText={confirmText}
          setFormState={setFormState}
          goToNext={onNext}
          isGP={isGP}
          mentalCapacityStatus={mentalCapacityStatus}
        />
      </div>
    </Modal>
  )
}

export default SignatureModal
